<template>
  <section class="prompt-manager">
    <div class="container" v-show="view_permissions.read == true">
      <SpinnerLoading v-if="!assistant" />
      <div v-else class="row justify-content-center">
        <div class="col-md-6 mt-6">
          <h2>Configuración inicial</h2>
          <p class="description mb-5">Agrega un conjunto de instrucciones o contextos personalizados para orientar las respuestas de la inteligencia artificial de tu chatbot.</p>
          <div class="label-box align-items-end mb-3">
            <label for="" class="mb-1">Escribe tu prompt</label>
            <span>{{ assistant.ia_configs.custom_prompt?.length || 0 }} / 3000</span>
          </div>
          <textarea
            id=""
            cols="30"
            rows="10"
            placeholder="Describe las instrucciones"
            :class="{ 'mb-3': true, active: assistant.ia_configs.custom_prompt?.length > 0 }"
            v-model="assistant.ia_configs.custom_prompt"
            maxlength="3000"
          />
          <label class="mb-3" for="">Comportamiento de la IA</label>
          <div class="two_box mb-5">
            <select
              class="w-100"
              id=""
              v-model="assistant.ia_configs.answer_length"
            >
              <option :value="null">Largo de respuesta</option>
              <option
                v-for="(item, index) in answer_length"
                :key="index"
                :value="item.value"
              >
                {{ item.name }}
              </option>
            </select>
            <select
              class="w-100"
              v-model="assistant.ia_configs.answer_tone"
            >
              <option :value="null">Tono de respuesta</option>
              <option
                v-for="(item, index) in answer_tone"
                :key="index"
                :value="item.value"
              >
                {{ item.name }}
              </option>
            </select>
          </div>
          <button class="btn btn-base md" @click="save">Guardar</button>
        </div>
        <div class="col-lg-4 mt-6">
          <DropdownTab
            v-for="(item, index) in items"
            :key="index"
            :item="item"
            :bgColor="'grey'"
            :dropBorder="true"
            :current_selected="selected_item"
            @selected="selected_item = $event"
          />
        </div>
      </div>
    </div>
    <NoAccessView :show="view_permissions.read != true" />
  </section>
</template>

<script>
import DropdownTab from '@/components/builders/DropdownTab';
import { mapState } from 'vuex';

export default {
  components: {
    DropdownTab
  },
  data() {
    return {
      selected_item: {},
      answer_length: [
        { value: "corta", name: 'Respuesta corta' },
        { value: 'media', name: 'Respuesta media' },
        { value: 'larga', name: 'Respuesta larga' },
      ],
      answer_tone: [
        { value: "informal", name: 'Tono informal' },
        { value: "amigable", name: 'Tono amigable' },
        { value: "directo", name: 'Tono directo' },
        { value: "seguro", name: 'Tono seguro' },
        { value: "profesional", name: 'Tono profesional' },
      ],
      assistant: null,
      items: [
        {
          name: '¿Qué es un Prompt?',
          drop: {
            show: false,
            info: [
              {
                list: [
                  {
                    text: 'Es una instrucción que le dice a la IA cómo debe responder. Mientras más claro y específico seas, mejores serán sus respuestas.'
                  }
                ]
              }
            ]
          }
        },
        {
          name: '¿Cómo funciona con el módulo “Documentos”?',
          drop: {
            show: false,
            info: [
              {
                text: 'La IA ya sabe elegir qué documento usar según la pregunta del usuario. Solo necesitas mencionar un documento en el prompt si quieres forzar su uso en ciertos casos. Por ejemplo:',
                list: [
                  { text: 'Cuando quieres que un documento tenga prioridad frente a una interacción.' },
                  { text: 'Cuando es importante asegurar que la respuesta sea desde un documento específico (como en cotizaciones o políticas).' },
                  {
                    title: 'Ejemplo:',
                    text: 'Si preguntan por devoluciones, responde con un resumen claro y deriva a un agente humano si es necesario. Para asegurar precisión, usa el documento “Política de cambios y devoluciones”.'
                  }
                ]
              }
            ]
          }
        },
        {
          name: '1. Define el propósito:',
          drop: {
            show: false,
            info: [
              {
                title: 'Ejemplo:',
                text: 'Que responda dudas de clientes sobre compras online, con un tono cercano y claro.',
              }
            ]
          }
        },
        {
          name: '2. Escribe el prompt:',
          drop: {
            show: false,
            info: [
              {
                title: 'El prompt debe incluir:',
                list: [
                  {
                    title: 'Rol:',
                    text: '¿Qué papel debe cumplir la IA? (ej: asistente de ventas)'
                  },
                  {
                    title: 'Tono:',
                    text: '¿Cómo debe hablar? (ej: amable, directo, técnico)'
                  },
                  {
                    title: 'Temas clave:',
                    text: '¿Cómo debe responder sobre ciertos temas importantes?'
                  },
                  {
                    title: 'Límites:',
                    text: '¿Qué cosas no debe decir o cuándo debe derivar?'
                  },
                  {
                    title: 'Documentos (si aplica)',
                    text: '¿Cuándo debe usar un documento específico?'
                  },
                  {
                    title: 'Ejemplo de prompt:',
                    text: 'Eres el asistente virtual de [Empresa], eres experto en [industria]. Responde las preguntas de nuestros clientes con un tono cercano y directo. Si preguntan por precios o cotizaciones, responde usando el documento “Precios y cotizaciones”. Si mencionan la palabra “demanda”, deriva de inmediato a un agente humano. No hables de marcas de la competencia. Refuerza siempre nuestros productos y servicios.'
                  },
                ]
              }
            ]
          }
        },
        {
          name: '3. Prueba y ajusta:',
          drop: {
            show: false,
            info: [
              {
                title: 'Simula conversaciones reales para validar los siguientes puntos:',
                list: [
                  { text: '¿Responde con el tono correcto?' },
                  { text: '¿Usa la información adecuada?' },
                  { text: '¿Deriva correctamente en temas delicados?' },
                  { text: '¿Entrega suficiente detalle?' },
                  {
                    title: "Ejemplos de prueba:",
                    list: [
                      { text: "¿Qué pasa si mi pedido no llega?" },
                      { text: "¿Cómo devuelvo un artículo?" },
                      { text: "¿Qué talla me recomiendan?" },
                      { text: '"Voy a presentar una demanda" → debería derivar' },
                      { text: '"¿Cuál es la mejor marca de [producto]?" → no debe mencionar competidores' }
                    ]
                  }
                ]
              }
            ]
          }
        },
        {
          name: '4. Usa variantes:',
          drop: {
            show: false,
            info: [
              {
                text: 'Si la IA no responde como esperabas, prueba con distintas formas de decir lo mismo.',
                list: [
                  { text: '"¿Qué medios de pago aceptan?"'},
                  { text: '"¿Puedo pagar con transferencia o solo con tarjeta?"' }
                ]
              }
            ]
          }
        }
      ]
    };
  },
  mounted() {
    this.initialize();
  },
  computed: {
    ...mapState(['selected_bot', 'view_permissions']),

    assistantId() {
      return this.selected_bot?.business_type?.assistant_id;
    },
  },
  methods: {
    async initialize() {
      let assistant = await this.$store.dispatch(
        'assistantsModule/listOne',
        this.assistantId,
      );
      if (!assistant.ia_configs) {
        assistant.ia_configs = {};
      }
      this.assistant = assistant;
      console.log("assistant->", this.assistant);
      
    },
    async save() {
      try {
        this.$swal({
          icon: 'info',
          title: 'Actualizando instrucciones..',
        });
        this.$swal.showLoading();

        await this.$store.dispatch('assistantsModule/update', { assistant_id: this.assistantId, payload: this.assistant });

        this.$swal({
            icon: 'success',
            title: 'Actualización exitosa',
          });
      } catch (error) {
        console.log("Error: ", error);
        this.$swal({
          icon: 'error',
          title: 'Error al actualizar',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.prompt-manager {
  width: 100%;
  padding-bottom: 5rem;

  .description {
    width: 80%;
    
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  .btn-base {
    width: 120px;
  }
  .scroll_box {
    max-height: 500px;
    overflow-y: scroll;
  }
}
</style>
